import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { selectAdminToken } from "../../admin/sessionSlice";
import { fetchThemeData } from "../../admin/themeSlice";
import { setBackButtonUrl } from "../uiSlice";
import { s3ThemeAssetUrl } from "../../utils/themeAssets";
import { themeButtonBackground, themeButtonTextColor } from "../../utils/colorUtils";
import { apiUrl } from "../../utils/apiUrl";
import { addReportToServer, secondsToHMS } from "../../functions/sendReport";
import { isDemoUser } from "../../utils/yogaResumeRoute";
import YogaThemedModal from "../../components/yoga/YogaThemedModal";
import likeThumb from "../../img/popup-like.png";
import ohnoImg from "../../img/ohno.png";
import treeImg from "../../img/tree.png";
import appleImg from "../../img/apple.png";
import "./game.css";

const GRATITUDE_MAX_LEN = 20;
const NAME_MAX_LEN = 30;
const APPLES_PER_PAGE = 10;

const FIELD_RULES = {
  name: { max: 30, hint: "Maximum 30 characters" },
  first_name: { min: 5, max: 30, hint: "Min 5, max 30 characters" },
  last_name: { min: 5, max: 30, hint: "Min 5, max 30 characters" },
  employee_id: {
    min: 5,
    max: 50,
    pattern: /^[a-zA-Z0-9]+$/,
    hint: "Min 5, max 50 characters; letters and numbers only",
  },
  phone: {
    min: 10,
    max: 10,
    pattern: /^\d{10}$/,
    hint: "Exactly 10 digits",
  },
  location: { min: 5, max: 50, hint: "Min 5, max 50 characters" },
  email: {
    pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    hint: "Enter a valid email address",
  },
};

function fieldKind(f) {
  const name = String(f?.name || "").toLowerCase();
  const type = String(f?.type || "").toLowerCase();
  if (type === "email" || name === "email") return "email";
  if (
    type === "tel" ||
    type === "phone" ||
    name === "phone" ||
    name === "mobile" ||
    name === "mobile_number" ||
    name === "phone_number"
  ) {
    return "phone";
  }
  return type || "text";
}

function getFieldRule(fieldName, kind) {
  const key = String(fieldName || "").toLowerCase();
  if (FIELD_RULES[key]) return FIELD_RULES[key];
  if (kind === "email") return FIELD_RULES.email;
  if (kind === "phone") return FIELD_RULES.phone;
  return null;
}

function validateByRule(val, rule, label) {
  if (!rule) return null;
  if (rule.min != null && val.length < rule.min) {
    return `"${label}" must be at least ${rule.min} characters.`;
  }
  if (rule.max != null && val.length > rule.max) {
    return `"${label}" must be at most ${rule.max} characters.`;
  }
  if (rule.pattern && !rule.pattern.test(val)) {
    if (label.toLowerCase().includes("employee")) {
      return `"${label}" must contain only letters and numbers.`;
    }
    if (label.toLowerCase().includes("phone") || label.toLowerCase().includes("mobile")) {
      return `"${label}" must be exactly 10 digits.`;
    }
    if (label.toLowerCase().includes("email")) {
      return `Please enter a valid email address for "${label}".`;
    }
    return `"${label}" format is invalid.`;
  }
  return null;
}

function sanitizeExtraInput(fieldName, kind, raw) {
  const key = String(fieldName || "").toLowerCase();
  if (key === "employee_id") return raw.replace(/[^a-zA-Z0-9]/g, "").slice(0, 50);
  if (kind === "phone" || key === "phone") return raw.replace(/\D/g, "").slice(0, 10);
  const rule = getFieldRule(fieldName, kind);
  if (rule?.max != null) return raw.slice(0, rule.max);
  return raw;
}

function appBaseUrl() {
  const b = (process.env.REACT_APP_BASE_URL || "").trim().replace(/\/+$/, "");
  return b || (typeof window !== "undefined" ? window.location.origin : "");
}

function demoPlansUrl() {
  return `${appBaseUrl()}/plans`;
}

/** Canopy branch positions for frontend/src/img/tree.png — on green foliage, two staggered rows */
const APPLE_SLOTS = [
  { top: "18%", left: "8%" },
  { top: "8%", left: "26%" },
  { top: "8%", left: "46%" },
  { top: "20%", left: "66%" },
  { top: "50%", left: "4%" },
  { top: "40%", left: "22%" },
  { top: "43%", left: "42%" },
  { top: "58%", left: "66%" },
  { top: "70%", left: "14%" },
  { top: "60%", left: "54%" },
];

const isTrue = (v) => String(v ?? "").trim().toLowerCase() === "true";

function formatFieldLabel(name) {
  return String(name || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function GamePage() {
  const dispatch = useDispatch();
  const { data: themeData, status, error: themeError } = useSelector((s) => s.theme);
  const user = useSelector((s) => s.auth.user);
  const adminToken = useSelector(selectAdminToken);

  const additionalOn = isTrue(themeData?.additional_fields);
  const extraFieldDefs = useMemo(() => {
    if (!additionalOn) return [];
    const list = Array.isArray(themeData?.additional_new_fields) ? themeData.additional_new_fields : [];
    return list.filter((f) => f && f.selected);
  }, [additionalOn, themeData]);

  const titleColor = themeData?.text_color || "#000000";
  const btnBg = themeButtonBackground(themeData);
  const btnColor = themeButtonTextColor(themeData) || "#ffffff";
  const btnStyle = {
    ...(btnBg && { backgroundColor: btnBg }),
    ...(btnColor && { color: btnColor }),
    ...(!btnBg && {
      backgroundImage: "linear-gradient(to right, #E25569, #FB9946)",
      color: "#ffffff",
    }),
  };

  const gameTitle =
    themeData?.game_page_title ?? themeData?.game_title ?? "Gratitude Tree";
  const gameSubTitle =
    themeData?.game_page_sub_title ??
    themeData?.game_sub_title ??
    "";

  const [name, setName] = useState("");
  const [gratitudeText, setGratitudeText] = useState("");
  const [extraValues, setExtraValues] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [elapsedSec, setElapsedSec] = useState(0);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [mainEl, setMainEl] = useState(null);

  const [posts, setPosts] = useState([]);
  const [loadingPosts, setLoadingPosts] = useState(true);
  const [page, setPage] = useState(1);
  const [selectedPost, setSelectedPost] = useState(null);
  const [likingId, setLikingId] = useState(null);
  const [showLikePopup, setShowLikePopup] = useState(false);
  const [showAlreadyLiked, setShowAlreadyLiked] = useState(false);
  const [alertModal, setAlertModal] = useState(null);

  const closeAlert = useCallback(() => setAlertModal(null), []);

  const showAlert = useCallback(
    (title, message, { success = false } = {}) => {
      setAlertModal({
        title,
        message,
        success,
        confirmText: "OK",
        onConfirm: closeAlert,
      });
    },
    [closeAlert]
  );

  useEffect(() => {
    setMainEl(document.querySelector(".ddd-user-main"));
  }, []);

  useEffect(() => {
    dispatch(setBackButtonUrl(user?.backButtonRedirect || "/login?&save=true"));
  }, [dispatch, user?.backButtonRedirect]);

  useEffect(() => {
    if (user?.name) setName((prev) => prev || String(user.name).slice(0, NAME_MAX_LEN));
  }, [user?.name]);

  useEffect(() => {
    const id = setInterval(() => setElapsedSec((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const totalPages = Math.max(1, Math.ceil(posts.length / APPLES_PER_PAGE));
  const pagedPosts = useMemo(
    () => posts.slice((page - 1) * APPLES_PER_PAGE, page * APPLES_PER_PAGE),
    [posts, page]
  );
  const hasSubmitted = useMemo(() => posts.some((p) => p.mine), [posts]);

  useEffect(() => {
    setPage((p) => Math.min(Math.max(1, p), totalPages));
  }, [totalPages]);

  const loadPosts = useCallback(async () => {
    if (!adminToken) {
      setLoadingPosts(false);
      return;
    }
    setLoadingPosts(true);
    try {
      const resp = await fetch(apiUrl("/gratitudeGallery"), {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${adminToken}` },
        body: JSON.stringify({ userId: user?.userId || "" }),
      });
      const raw = await resp.text();
      let payload;
      try {
        payload = raw ? JSON.parse(raw) : {};
      } catch {
        throw new Error(resp.ok ? "Invalid server response" : `Server error (${resp.status})`);
      }
      if (!resp.ok) {
        throw new Error(payload?.error || `Failed to load posts (${resp.status})`);
      }
      setPosts(payload?.success && Array.isArray(payload.posts) ? payload.posts : []);
    } catch (e) {
      console.error("gratitudeGallery", e);
      setPosts([]);
    } finally {
      setLoadingPosts(false);
    }
  }, [adminToken, user?.userId]);

  useEffect(() => {
    loadPosts();
  }, [loadPosts]);

  const handleSubmit = async () => {
    if (!user || !adminToken) {
      showAlert("Not signed in", "Please reload and sign in again.");
      return;
    }
    const nameVal = name.trim();
    if (!nameVal) {
      showAlert("Name required", "Please enter your name.");
      return;
    }
    const nameErr = validateByRule(nameVal, FIELD_RULES.name, "Your Name");
    if (nameErr) {
      showAlert("Invalid name", nameErr);
      return;
    }
    if (!gratitudeText.trim()) {
      showAlert("Required", "Please fill the field before submitting!");
      return;
    }
    for (const f of extraFieldDefs) {
      const val = String(extraValues[f.name] ?? "").trim();
      const label = formatFieldLabel(f.name);
      if (!val) {
        showAlert("Missing field", `Please fill in "${label}".`);
        return;
      }
      const kind = fieldKind(f);
      const rule = getFieldRule(f.name, kind);
      const err = validateByRule(val, rule, label);
      if (err) {
        showAlert("Invalid field", err);
        return;
      }
    }

    setSubmitting(true);
    const submittedAnswer = gratitudeText.trim().slice(0, GRATITUDE_MAX_LEN);
    const trimmedExtra = extraFieldDefs.length
      ? Object.fromEntries(
          extraFieldDefs.map((f) => [f.name, String(extraValues[f.name] ?? "").trim()])
        )
      : null;
    try {
      const resp = await fetch(apiUrl("/gratitudeSubmit"), {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${adminToken}` },
        body: JSON.stringify({
          userId: user.userId,
          name: nameVal.slice(0, NAME_MAX_LEN),
          email: user.email || "",
          answer: submittedAnswer,
          time: secondsToHMS(elapsedSec),
          extraFields: trimmedExtra,
        }),
      });
      const raw = await resp.text();
      let payload;
      try {
        payload = raw ? JSON.parse(raw) : {};
      } catch {
        throw new Error(resp.ok ? "Invalid server response" : `Server error (${resp.status}). Restart the backend server.`);
      }
      if (!resp.ok || !payload?.success) {
        showAlert("Could not submit", payload?.error || "Something went wrong.");
        setSubmitting(false);
        return;
      }

      setGratitudeText("");
      setExtraValues({});

      addReportToServer(user, {
        points: 0,
        elapsedSeconds: elapsedSec,
        answers: [submittedAnswer],
        standalone: true,
      });

      await loadPosts();
      setPage(1);

      if (isDemoUser(user)) {
        setAlertModal({
          title: "Thank you for playing.",
          message: "Subscribe to any PLAN to play with your peers.",
          success: true,
          confirmText: "OK",
          onConfirm: () => {
            closeAlert();
            window.location.href = demoPlansUrl();
          },
        });
      } else {
        showAlert("Submitted!", "Your gratitude has been added to the tree.", { success: true });
      }
    } catch (e) {
      showAlert("Network error", String(e.message || e));
    } finally {
      setSubmitting(false);
    }
  };

  const handleLike = async (gameId) => {
    if (!user || !adminToken || likingId) return;
    const target = posts.find((p) => p.id === gameId);
    if (target?.likedByMe) {
      setShowAlreadyLiked(true);
      return;
    }

    setLikingId(gameId);
    try {
      const resp = await fetch(apiUrl("/yogaLike"), {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${adminToken}` },
        body: JSON.stringify({ userId: user.userId, gameId }),
      });
      const payload = await resp.json();
      if (resp.ok && payload?.success) {
        const updater = (p) =>
          p.id === gameId ? { ...p, likedByMe: true, likeCount: payload.likeCount ?? p.likeCount + 1 } : p;
        setPosts((prev) => prev.map(updater));
        setSelectedPost((prev) => (prev?.id === gameId ? updater(prev) : prev));
        setShowLikePopup(true);
      }
    } catch (e) {
      console.error("yogaLike", e);
    } finally {
      setLikingId(null);
    }
  };

  const handleDelete = (gameId) => {
    setAlertModal({
      title: "Delete this post?",
      message: "This cannot be undone.",
      confirmText: "Yes, delete",
      cancelText: "Cancel",
      onCancel: closeAlert,
      onConfirm: async () => {
        closeAlert();
        try {
          const resp = await fetch(apiUrl("/gratitudeDelete"), {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${adminToken}` },
            body: JSON.stringify({ userId: user.userId, gameId }),
          });
          const payload = await resp.json();
          if (resp.ok && payload?.success) {
            setPosts((prev) => prev.filter((p) => p.id !== gameId));
            setSelectedPost(null);
          } else {
            showAlert("Error", payload?.error || "Could not delete.");
          }
        } catch (e) {
          showAlert("Network error", String(e.message || e));
        }
      },
    });
  };

  if (status === "loading" || status === "idle") {
    return (
      <div className="quiz-loader-container">
        <div className="quiz-loader" />
      </div>
    );
  }

  if (status === "failed" || !themeData) {
    return (
      <div className="gt-page">
        <p style={{ textAlign: "center", color: titleColor }}>
          Could not load game data.{themeError ? ` (${themeError})` : ""}
        </p>
        <button
          type="button"
          className="gt-submit"
          style={{ margin: "16px auto", display: "block", ...btnStyle }}
          onClick={() => dispatch(fetchThemeData({ themeId: null }))}
        >
          Retry
        </button>
      </div>
    );
  }

  const rules = Array.isArray(themeData?.rules) ? themeData.rules : [];
  const themeLogo = themeData?.logo ? s3ThemeAssetUrl(themeData.logo) : null;

  return (
    <div className="gt-page">
      {mainEl && rules.length > 0
        ? createPortal(
            <button type="button" className="cg-rules-btn" style={btnStyle} onClick={() => setRulesOpen(true)}>
              How to play
            </button>,
            mainEl
          )
        : null}

      {themeLogo ? (
        <div className="gt-logo-wrap">
          <img src={themeLogo} alt="" className="gt-logo" referrerPolicy="no-referrer" />
        </div>
      ) : null}

      <h1 className="gt-title" style={{ color: titleColor }}>
        {gameTitle}
      </h1>
      {gameSubTitle ? (
        <p className="gt-subtitle" style={{ color: titleColor }}>
          {gameSubTitle}
        </p>
      ) : null}

      {!loadingPosts && !hasSubmitted ? (
      <div className="gt-form">
        <div className="gt-form-row">
          <label className="gt-label" style={{ color: titleColor }}>
            Your Name
          </label>
          <input
            className="gt-input"
            value={name}
            maxLength={NAME_MAX_LEN}
            onChange={(e) => setName(e.target.value.slice(0, NAME_MAX_LEN))}
            placeholder="Your name"
          />
          <p className="gt-field-hint">{FIELD_RULES.name.hint}</p>
        </div>

        <div className="gt-form-row">
          <label className="gt-label" style={{ color: titleColor }}>
            Insert your reasons to be happy and grateful
          </label>
          <input
            className="gt-input"
            value={gratitudeText}
            maxLength={GRATITUDE_MAX_LEN}
            placeholder="Share your thoughts"
            onChange={(e) => setGratitudeText(e.target.value.replace(/^\s+/, "").slice(0, GRATITUDE_MAX_LEN))}
          />
          <p className="gt-field-hint">Maximum character limit is {GRATITUDE_MAX_LEN}</p>
        </div>

        {extraFieldDefs.map((f) => {
          const kind = fieldKind(f);
          const rule = getFieldRule(f.name, kind);
          const inputType = kind === "email" ? "email" : kind === "phone" ? "tel" : "text";
          return (
            <div className="gt-form-row" key={f.name}>
              <label className="gt-label" style={{ color: titleColor }}>
                {formatFieldLabel(f.name)}
              </label>
              <input
                className="gt-input"
                type={inputType}
                inputMode={kind === "phone" ? "numeric" : undefined}
                autoComplete={kind === "email" ? "email" : kind === "phone" ? "tel" : undefined}
                maxLength={rule?.max}
                value={extraValues[f.name] ?? ""}
                placeholder={formatFieldLabel(f.name)}
                onChange={(e) => {
                  const next = sanitizeExtraInput(f.name, kind, e.target.value);
                  setExtraValues((prev) => ({ ...prev, [f.name]: next }));
                }}
              />
              {rule?.hint ? <p className="gt-field-hint">{rule.hint}</p> : null}
            </div>
          );
        })}

        <button
          type="button"
          className="gt-submit"
          style={btnStyle}
          onClick={handleSubmit}
          disabled={submitting}
        >
          {submitting ? "Submitting…" : "Submit"}
        </button>
      </div>
      ) : null}

      <div className="gt-tree-wrap">
        <img src={treeImg} alt="Gratitude tree" className="gt-tree" />
        <div className="gt-apples-layer">
          {!loadingPosts &&
            pagedPosts.map((post, i) => {
              const slot = APPLE_SLOTS[i];
              if (!slot) return null;
              return (
                <button
                  key={post.id}
                  type="button"
                  className="gt-apple"
                  style={{
                    top: slot.top,
                    left: slot.left,
                    backgroundImage: `url(${appleImg})`,
                  }}
                  onClick={() => setSelectedPost(post)}
                  title={post.answer}
                >
                  <span>{post.answer}</span>
                </button>
              );
            })}
        </div>
      </div>

      {loadingPosts ? (
        <div className="quiz-loader-container">
          <div className="quiz-loader" />
        </div>
      ) : posts.length === 0 ? (
        <p className="gt-empty-posts">No Post. Be the first one.</p>
      ) : null}

      {totalPages > 1 ? (
        <div className="gt-pagination">
          <button
            type="button"
            className="gt-page-btn"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Previous
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
            <button
              type="button"
              key={n}
              className={`gt-page-num${n === page ? " gt-page-num--active" : ""}`}
              style={
                n === page
                  ? { backgroundColor: btnBg || "#E25569", color: btnColor || "#fff", borderColor: btnBg || "#E25569" }
                  : undefined
              }
              onClick={() => setPage(n)}
            >
              {n}
            </button>
          ))}
          <button
            type="button"
            className="gt-page-btn"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          >
            Next
          </button>
        </div>
      ) : null}

      {selectedPost ? (
        <div className="cg-modal-backdrop" onClick={() => setSelectedPost(null)}>
          <div className="gt-post-modal" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="cg-modal-close" onClick={() => setSelectedPost(null)}>
              &times;
            </button>
            {selectedPost.mine ? (
              <button
                type="button"
                className="gt-post-delete"
                aria-label="Delete post"
                title="Delete post"
                onClick={() => handleDelete(selectedPost.id)}
              >
                <i className="fa-solid fa-trash" aria-hidden />
              </button>
            ) : null}
            <div className="gt-post-modal-apple" style={{ backgroundImage: `url(${appleImg})` }}>
              <span>{selectedPost.answer}</span>
            </div>
            <div className="gt-post-modal-footer">
              <button
                type="button"
                className={`gt-like-btn${selectedPost.likedByMe ? " gt-like-btn--on" : ""}`}
                onClick={() => handleLike(selectedPost.id)}
                disabled={likingId === selectedPost.id}
              >
                <i className="fa-solid fa-thumbs-up" /> {selectedPost.likeCount}
              </button>
              <span className="gt-post-author">{selectedPost.name || "Anonymous"}</span>
            </div>
          </div>
        </div>
      ) : null}

      {rulesOpen && (
        <div className="cg-modal-backdrop" onClick={() => setRulesOpen(false)}>
          <div className="cg-modal" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="cg-modal-close cg-modal-close--red" onClick={() => setRulesOpen(false)}>
              &times;
            </button>
            <h3 style={{ marginTop: 0 }}>How to play</h3>
            <ul className="cg-rules-list">
              {rules.map((rule, i) => (
                <li key={i}>{rule}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {showLikePopup && (
        <div className="cg-modal-backdrop" onClick={() => setShowLikePopup(false)}>
          <div className="cg-like-popup" onClick={(e) => e.stopPropagation()}>
            <img className="cg-like-popup-thumb" src={likeThumb} alt="Thumbs up" />
            <div className="cg-like-popup-text">Thank you for liking!</div>
            <button type="button" className="cg-like-popup-ok" onClick={() => setShowLikePopup(false)}>
              OK
            </button>
          </div>
        </div>
      )}

      {showAlreadyLiked && (
        <div className="cg-modal-backdrop" onClick={() => setShowAlreadyLiked(false)}>
          <div className="cg-like-popup" onClick={(e) => e.stopPropagation()}>
            <img className="cg-ohno-img" src={ohnoImg} alt="Already liked" />
            <button type="button" className="cg-like-popup-ok" onClick={() => setShowAlreadyLiked(false)}>
              OK
            </button>
          </div>
        </div>
      )}

      <YogaThemedModal
        open={Boolean(alertModal)}
        title={alertModal?.title ?? ""}
        titleId="gt-alert-title"
        themeData={themeData}
        confirmText={alertModal?.confirmText ?? "OK"}
        cancelText={alertModal?.cancelText}
        onConfirm={alertModal?.onConfirm}
        onCancel={alertModal?.onCancel}
      >
        {alertModal?.success ? (
          <div
            className="gt-alert-icon"
            style={{
              backgroundColor: btnBg || "#22c55e",
              color: btnColor || "#fff",
            }}
            aria-hidden
          >
            ✓
          </div>
        ) : null}
        <p>{alertModal?.message}</p>
      </YogaThemedModal>
    </div>
  );
}
